import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Pagination from '../../utils/pagination';
import { useLanguage } from '../../contexts/LanguageContext';
import { getApi } from '../../utils/api/api';
import Loader from '../../utils/loader';
import { showToast } from '../../utils/toast';

const UsersList = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [usersData, setUsersData] = useState({
        data: [],
        total: 0,
        per_page: 9,
        current_page: 1,
    });
    const [currentPage, setCurrentPage] = useState(1);

    const fetchUsers = async (page = 1) => {
        setLoading(true);
        try {
            const response = await getApi(`admin/users?page=${page}`);
            
            if (response.data && response.data.users) {
                setUsersData({
                    data: response.data.users.data,
                    total: response.data.users.total,
                    per_page: response.data.users.per_page,
                    current_page: response.data.users.current_page,
                });
            } else {
                console.error("Unexpected API response:", response);
                setUsersData({ data: [], total: 0, per_page: 9, current_page: 1 });
                showToast("error", "Unexpected data format received");
            }
        } catch (error) {
            console.error("Failed to fetch users:", error);
            setUsersData({ data: [], total: 0, per_page: 9, current_page: 1 });
            showToast("error", "Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(currentPage);
    }, [currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            active: { color: 'success', text: 'Active' },
            inactive: { color: 'secondary', text: 'Inactive' },
            pending: { color: 'warning', text: 'Pending' },
            banned: { color: 'danger', text: 'Banned' }
        };
        
        const statusInfo = statusMap[status] || { color: 'info', text: 'Unknown' };
        
        return (
            <span className={`badge bg-${statusInfo.color} rounded-pill px-3 py-1`}>
                {statusInfo.text}
            </span>
        );
    };

    return (
        <div className="container-fluid py-4">
            <div className="card shadow-lg border-0">
                <div className="card-header bg-white border-0 pt-4 pb-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h2 className="mb-0 text-primary">
                            <i className="bi bi-people-fill me-2"></i>
                            {t('usersList.heading', 'Users Management')}
                        </h2>
                        <div className="d-flex">
                            <div className="input-group input-group-outline">
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Search users..."
                                />
                                <button className="btn btn-primary">
                                    <i className="bi bi-search"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-body px-0 pb-2">
                    {loading ? (
                        <Loader />
                    ) : usersData.data.length > 0 ? (
                        <>
                            <div className="table-responsive">
                                <table className="table align-items-center mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">
                                                {t('usersList.name', 'Name')}
                                            </th>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                                Email
                                            </th>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                                {t('usersList.companyName', 'Company')}
                                            </th>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                                {t('usersList.contact', 'Phone')}
                                            </th>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                                                Status
                                            </th>
                                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">
                                                {t('usersList.actions', 'Actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usersData.data.map((user, index) => (
                                            <tr key={user.id}>
                                                <td className="ps-4">
                                                    <p className="text-sm font-weight-bold mb-0">
                                                        {user.name}
                                                    </p>
                                                </td>
                                                <td>
                                                    <p className="text-sm mb-0">
                                                        {user.email}
                                                    </p>
                                                </td>
                                                <td>
                                                    <p className="text-sm font-weight-bold mb-0">
                                                        {user.c_name || 'N/A'}
                                                    </p>
                                                </td>
                                                <td>
                                                    <p className="text-sm mb-0">
                                                        <i className="bi bi-telephone me-2 text-primary"></i>
                                                        {user.phone_number || 'N/A'}
                                                    </p>
                                                </td>
                                                <td>
                                                    {getStatusBadge(user.status || 'active')}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-primary rounded-circle me-2"
                                                        onClick={() => navigate(`/admin/users/${user.id}`, { state: { user } })}
                                                        data-bs-toggle="tooltip"
                                                        title="View Details"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </button>
                                                    {/* <button
                                                        className="btn btn-sm btn-outline-secondary rounded-circle me-2"
                                                        onClick={() => navigate(`/admin/users/edit/${user.id}`)}
                                                        data-bs-toggle="tooltip"
                                                        title="Edit User"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button> */}
                                                    {/* <button
                                                        className="btn btn-sm btn-outline-danger rounded-circle"
                                                        onClick={() => {}}
                                                        data-bs-toggle="tooltip"
                                                        title="Delete User"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button> */}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center px-4 pt-3">
                                <div className="text-sm text-muted">
                                    Showing <b>{usersData.data.length}</b> of <b>{usersData.total}</b> users
                                </div>
                                <Pagination
                                    currentPage={usersData.current_page}
                                    totalPages={Math.ceil(usersData.total / usersData.per_page)}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-5">
                            <i className="bi bi-people display-4 text-muted mb-3"></i>
                            <h4 className="text-muted mb-3">{t('usersList.noUsers', 'No users found')}</h4>
                            <button 
                                className="btn btn-primary"
                                onClick={() => fetchUsers(1)}
                            >
                                <i className="bi bi-arrow-repeat me-2"></i>
                                Refresh
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UsersList;